import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useReportError } from '@/hooks/useReportError';
import { useEstateHoldingsContext } from '@/modules/estate/contexts/estateHoldings.context';
import {
  EstateOnboardingContextSuggestion,
  EstateOnboardingSuggestionGroup,
  isEstateOnboardingReviewCardSuggestion,
} from '@/modules/estate/estateAIOnboarding/estateOnboarding.types';
import {
  processEstateOnboardingSuggestionsDataV2,
  useFetchEstateOnboardingSuggestions,
} from '@/modules/estate/estateAIOnboarding/hooks/useFetchEstateOnboardingSuggestions';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { useOnWorkflowTrackerComplete } from '@/modules/workflowTrackers/hooks/useOnWorkflowTrackerComplete';
import { WorkflowTrackerKind } from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';

import {
  EMPTY_GROUPS,
  groupEstateOnboardingSuggestionsByGroup,
  isEstateHoldingSuggestion,
} from '../estateOnboarding.utils';
import {
  EstateOnboardingUnacknowledgedSuggestionsContext,
  EstateOnboardingUnacknowledgedSuggestionsContextShape,
} from './estateOnboardingUnacknowledgedSuggestions.context';

function useContextValue(): EstateOnboardingUnacknowledgedSuggestionsContextShape {
  const { householdId } = useHouseholdDetailsContext();
  const {
    entities: estateEntities,
    loading: estatePageLoading,
    refetch: refetchEstatePageData,
    unacknowledgedSuggestionsV2,
  } = useEstateHoldingsContext();
  const { reportError } = useReportError();

  const [onboardingSuggestionsToReview, setOnboardingSuggestionsToReview] =
    useState<EstateOnboardingContextSuggestion[]>([]);

  const lastGroupingSignatureRef = useRef<string | null>(null);

  const [suggestionsByGroup, setSuggestionsByGroup] =
    useState<
      EstateOnboardingUnacknowledgedSuggestionsContextShape['suggestionsToReviewByGroup']
    >(EMPTY_GROUPS);

  const { loading, refetch } = useFetchEstateOnboardingSuggestions({
    householdId,
    skip: !householdId,
    onCompleted: (data) => {
      const suggestions = processEstateOnboardingSuggestionsDataV2(data);

      diagnostics.debug(
        '[EstateOnboardingUnacknowledgedSuggestionsProvider][useFetchEstateOnboardingSuggestions][onCompleted]',
        {
          suggestions: suggestions.length,
        }
      );

      setOnboardingSuggestionsToReview(suggestions);
    },
    onError: (error) => {
      reportError(
        '[EstateOnboardingUnacknowledgedSuggestionsProvider] failed to fetch suggestions',
        error
      );
    },
  });

  const holdingsSuggestionsToReview = useMemo(
    () =>
      Object.values(unacknowledgedSuggestionsV2)
        .flat()
        .filter(isEstateHoldingSuggestion),
    [unacknowledgedSuggestionsV2]
  );

  const suggestionsToReview = useMemo(
    () => [...onboardingSuggestionsToReview, ...holdingsSuggestionsToReview],
    [holdingsSuggestionsToReview, onboardingSuggestionsToReview]
  );

  const refetchSuggestions = useCallback(() => {
    void refetch();
    void refetchEstatePageData?.();
  }, [refetch, refetchEstatePageData]);

  const refetchSuggestionsOnWorkflowCompletion = useCallback(
    (workflowKind: WorkflowTrackerKind) => {
      diagnostics.debug(
        '[EstateOnboardingUnacknowledgedSuggestionsProvider] Workflow completed, refetching suggestions',
        {
          householdId,
          workflowKind,
        }
      );
      refetchSuggestions();
    },
    [householdId, refetchSuggestions]
  );

  // Watch for document extraction workflow completion to refetch suggestions
  useOnWorkflowTrackerComplete({
    kind: WorkflowTrackerKind.DocumentExtraction,
    onCompleted: () => {
      refetchSuggestionsOnWorkflowCompletion(
        WorkflowTrackerKind.DocumentExtraction
      );
    },
    enabled: !!householdId,
  });

  // Watch for household suggestion regeneration completion to refetch suggestions
  useOnWorkflowTrackerComplete({
    kind: WorkflowTrackerKind.AiSuggestionsV2HouseholdRegen,
    onCompleted: () => {
      refetchSuggestionsOnWorkflowCompletion(
        WorkflowTrackerKind.AiSuggestionsV2HouseholdRegen
      );
    },
    enabled: !!householdId,
  });

  useEffect(() => {
    if (!suggestionsToReview?.length) {
      diagnostics.debug(
        '[EstateOnboardingUnacknowledgedSuggestionsProvider][suggestionsByGroup] no suggestions'
      );
      setSuggestionsByGroup(EMPTY_GROUPS);
      lastGroupingSignatureRef.current = null;
      return;
    }

    const grouped = groupEstateOnboardingSuggestionsByGroup(
      suggestionsToReview.filter(isEstateOnboardingReviewCardSuggestion),
      estateEntities || []
    );

    const signature = generateGroupedSuggestionsSignature(grouped);

    if (signature === lastGroupingSignatureRef.current) {
      diagnostics.debug(
        '[EstateOnboardingUnacknowledgedSuggestionsProvider][suggestionsByGroup] same signature. skipping state update'
      );
      return;
    }

    lastGroupingSignatureRef.current = signature;

    diagnostics.debug(
      '[EstateOnboardingUnacknowledgedSuggestionsProvider][suggestionsByGroup] new signature! updating state',
      { suggestionsByGroup: grouped }
    );
    setSuggestionsByGroup(grouped);
  }, [estateEntities, suggestionsToReview]);

  const countsByGroup = useMemo(() => {
    return {
      [EstateOnboardingSuggestionGroup.DirectHoldings]:
        suggestionsByGroup[EstateOnboardingSuggestionGroup.DirectHoldings]
          .length,
      [EstateOnboardingSuggestionGroup.TrustEntities]:
        suggestionsByGroup[EstateOnboardingSuggestionGroup.TrustEntities]
          .length,
      [EstateOnboardingSuggestionGroup.TrustHoldings]:
        suggestionsByGroup[EstateOnboardingSuggestionGroup.TrustHoldings]
          .length,
      [EstateOnboardingSuggestionGroup.BusinessEntities]:
        suggestionsByGroup[EstateOnboardingSuggestionGroup.BusinessEntities]
          .length,
      [EstateOnboardingSuggestionGroup.BusinessHoldings]:
        suggestionsByGroup[EstateOnboardingSuggestionGroup.BusinessHoldings]
          .length,
    } as const;
  }, [suggestionsByGroup]);

  const totalCount = useMemo(
    () => Object.values(countsByGroup).reduce((sum, n) => sum + (n ?? 0), 0),
    [countsByGroup]
  );

  return {
    suggestionsToReview,
    suggestionsToReviewByGroup: suggestionsByGroup,
    additionalReferenceSuggestions: [],
    countsByGroup,
    totalCount,
    loading: loading || estatePageLoading,
    refetch: refetchSuggestions,
  };
}
export function EstateOnboardingUnacknowledgedSuggestionsProvider({
  children,
}: PropsWithChildren) {
  const value = useContextValue();

  return (
    <EstateOnboardingUnacknowledgedSuggestionsContext.Provider value={value}>
      {children}
    </EstateOnboardingUnacknowledgedSuggestionsContext.Provider>
  );
}

/**
 * Generates a signature string for grouped suggestions to detect changes.
 *
 * The signature is a deterministic string representation of which suggestion IDs
 * are in which groups. This allows efficient comparison to determine if the
 * grouped suggestions have changed without doing a deep equality check.
 *
 * Format: "GroupKey1:id1,id2|GroupKey2:id3,id4|GroupKey3:id5"
 *
 * @param grouped - The grouped suggestions object with group keys and suggestion arrays
 * @returns A deterministic signature string representing the grouping structure
 */
function generateGroupedSuggestionsSignature(
  grouped: EstateOnboardingUnacknowledgedSuggestionsContextShape['suggestionsToReviewByGroup']
): string {
  return Object.entries(grouped)
    .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
    .map(([groupKey, groupSuggestions]) => {
      const ids = groupSuggestions
        .map((s) => s.id)
        .sort()
        .join(',');
      return `${groupKey}:${ids}`;
    })
    .join('|');
}
